<template>
  <div class="d-none d-md-block site-search-wrapper">
    <v-autocomplete
      flat
      density="compact"
      hide-details
      v-model:search="query"
      :items="hits"
      hide-selected
      hide-no-data
      item-title="label"
      item-value="ident"
      :label="t('toolbar.search')"
      return-object
      clearable
      variant="solo"
      @update:model-value="goToHit"
    >
      <template v-slot:prepend-inner>
        <v-icon>mdi-magnify</v-icon>
      </template>
      <template v-slot:item="{ props, item }">
        <v-list-item v-bind="props">
          <template v-slot:prepend>
            <v-avatar>
              <v-icon v-if="item.raw.type == 'merchant'">mdi-store</v-icon>
              <v-icon v-else :class="colorFromPhase(item.raw.more.phase)">
                {{ t("campaign.icon." + item.raw.type) }}</v-icon
              >
            </v-avatar>
          </template>
          <v-list-item-subtitle v-if="item.raw.type == 'merchant'">{{
            item.raw.more.brand_name
          }}</v-list-item-subtitle>
          <v-list-item-subtitle v-else>
            {{ item.raw.more.merchant }},
            {{ formatDate(item.raw.more.valid_from) }} -
            {{ formatDate(item.raw.more.valid_until) }};
            {{ t("campaign.list.status__" + item.raw.more.current_status) }}
          </v-list-item-subtitle>
        </v-list-item>
      </template>
    </v-autocomplete>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useMerchantStore } from '@/stores/merchant'
import { getSiteSearch } from '@/api/search.js'
import { formatDate } from '@/utils/format.js'

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()
const merchantStore = useMerchantStore()

const query = ref('')
const hits = ref([])
const timeout = ref(undefined)

const currentMerchant = computed(() => authStore.currentMerchant)

watch(query, (val) => {
  if (val && val.length > 1) {
    siteSearch()
  }
})

const siteSearch = async () => {
  if (timeout.value) clearTimeout(timeout.value)

  timeout.value = setTimeout(async () => {
    const results = await getSiteSearch(query.value)
    if (results.length == 1 && results[0].by_uuid) {
      goToHit(results[0])
    } else {
      hits.value = results
    }
  }, 500)
}

const goToHit = async (hit) => {
  if (!hit) return

  let to
  if (hit.type === 'merchant') {
    to = {
      name: 'dashboard',
    }
  } else {
    to = {
      params: {
        merchantIdent: hit.merchantIdent,
        campaignIdent: hit.ident,
      },
    }
    if (hit.type.match('^sweepstake')) {
      to.name = 'sweepstake-review'
    } else if (hit.type.match('^coupon')) {
      to.name = 'coupon-review'
    }
  }

  if (currentMerchant.value !== hit.merchantIdent) {
    await merchantStore.setCurrentFromIdent(hit.merchantIdent)
  }

  hits.value = []
  query.value = ''

  router.push(to)
}

const colorFromPhase = (phase) => {
  switch (phase) {
    case 'running':
      return 'text-light-green-lighten-2'
    case 'pending':
      return 'text-yellow-lighten-2'
    case 'ended':
      return 'text-blue-grey-lighten-2'
  }
}
</script>

<style scoped>
.site-search-wrapper {
  width: 300px;
}
/* light blue at rest, white on focus */
.site-search-wrapper :deep(.v-field) {
  background-color: rgba(255, 255, 255, 0.16);
  color: #ffffff;
  transition: background-color 0.2s, color 0.2s;
}
.site-search-wrapper :deep(.v-field .v-label) {
  color: #ffffff;
  opacity: 1;
}
.site-search-wrapper :deep(.v-field .v-icon) {
  color: #ffffff;
}
.site-search-wrapper :deep(.v-field--focused) {
  background-color: #ffffff;
  color: rgba(0, 0, 0, 0.87);
}
.site-search-wrapper :deep(.v-field--focused .v-label) {
  color: rgba(0, 0, 0, 0.6);
  opacity: 1;
}
.site-search-wrapper :deep(.v-field--focused .v-icon) {
  color: rgba(0, 0, 0, 0.54);
}
/* prevent the solo overlay from overriding these colours on hover/focus */
.site-search-wrapper :deep(.v-field__overlay) {
  display: none;
}
</style>
